/**
 * Bookmarks, grouped by category. Categories carry the work-mode filter, so
 * switching mode changes which groups appear.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Plus, ExternalLink, Trash2, FolderPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWorkMode } from "@/hooks/use-work-mode";

export default function BookmarksPage() {
  const { workMode } = useWorkMode();
  const { toast } = useToast();

  const groups = useQuery(api.categories.listWithItems, { workMode });
  const categories = useQuery(api.categories.list, {});
  const createItem = useMutation(api.items.create);
  const removeItem = useMutation(api.items.remove);
  const touch = useMutation(api.items.touch);
  const createCategory = useMutation(api.categories.create);

  const [itemOpen, setItemOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center gap-2">
        <h1 className="page-title text-2xl font-semibold flex-1">Bookmarks</h1>

        <Dialog open={categoryOpen} onOpenChange={setCategoryOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <FolderPlus className="w-4 h-4 mr-1.5" />
              Category
            </Button>
          </DialogTrigger>
          <CategoryDialog
            onSubmit={async (name) => {
              await createCategory({ name });
              setCategoryOpen(false);
            }}
          />
        </Dialog>

        <Dialog open={itemOpen} onOpenChange={setItemOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-1.5" />
              Bookmark
            </Button>
          </DialogTrigger>
          <ItemDialog
            categories={categories ?? []}
            onSubmit={async (values) => {
              try {
                await createItem(values);
                setItemOpen(false);
              } catch (error) {
                toast({
                  title: "Could not save bookmark",
                  description: String(error),
                  variant: "destructive",
                });
              }
            }}
          />
        </Dialog>
      </div>

      {groups === undefined && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {groups?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              No categories visible in {workMode} mode.
            </p>
            <p className="text-xs text-muted-foreground">
              Create a category, or switch work mode in the top bar.
            </p>
          </CardContent>
        </Card>
      )}

      {groups?.map((group) => (
        <Card key={group._id}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: group.color }}
                aria-hidden="true"
              />
              {group.name}
              <span className="text-xs font-normal text-muted-foreground">
                {group.items.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {group.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing here yet.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((item) => (
                  <div
                    key={item._id}
                    className="flex min-h-11 items-center rounded-lg border border-border hover:border-primary/50 transition-colors min-w-0 overflow-hidden"
                  >
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        touch({ id: item._id }).catch(() => {});
                      }}
                      className="flex min-h-11 items-center gap-2 min-w-0 flex-1 px-3 py-2"
                    >
                      <ExternalLink className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                      <span className="text-sm truncate">{item.name}</span>
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${item.name}`}
                      className="h-11 w-11 shrink-0 text-destructive hover:text-destructive"
                      onClick={() => removeItem({ id: item._id })}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CategoryDialog({
  onSubmit,
}: {
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState("");

  return (
    <DialogContent className="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>New category</DialogTitle>
      </DialogHeader>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!name.trim()) return;
          await onSubmit(name.trim());
          setName("");
        }}
        className="space-y-4"
      >
        <div className="space-y-1.5">
          <Label htmlFor="category-name">Name</Label>
          <Input
            id="category-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Documentation"
            autoFocus
            required
          />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={!name.trim()}>
            Create
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

const NO_CATEGORY = "__none__";

function ItemDialog({
  categories,
  onSubmit,
}: {
  categories: Array<{ _id: Id<"categories">; name: string }>;
  onSubmit: (values: {
    name: string;
    url: string;
    categoryIds?: Id<"categories">[];
  }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [categoryId, setCategoryId] = useState<string>(NO_CATEGORY);

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>New bookmark</DialogTitle>
      </DialogHeader>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!name.trim() || !url.trim()) return;
          await onSubmit({
            name: name.trim(),
            url: url.trim(),
            categoryIds:
              categoryId === NO_CATEGORY
                ? undefined
                : [categoryId as Id<"categories">],
          });
          setName("");
          setUrl("");
        }}
        className="space-y-4"
      >
        <div className="space-y-1.5">
          <Label htmlFor="item-name">Name</Label>
          <Input
            id="item-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="item-url">URL</Label>
          <Input
            id="item-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="item-category">Category</Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger id="item-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_CATEGORY}>Uncategorised</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category._id} value={category._id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={!name.trim() || !url.trim()}>
            Save
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
